import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { useParams, useSearchParams } from 'react-router-dom';
import { CyclesCountResponse } from '../../../../types/cycle';
import {
  CYCLE_STATUS_TO_NUMBER,
  CYCLE_TAB_BY_STATUS_QUERY_KEY,
  DEFAULT_STATUS_TAB,
  NO_TAB_SELECTED,
  TAB_NUMBER_TO_STATUS,
} from '../../constants';
import CycleStatus from '../../../../enums/cycle-status.enum';
import { EXTENDED_INTERVAL_UPDATE_DATA } from '../../../../constants/common';
import { usePolling } from '../../../../hooks/usePolling';

export interface IContractScreensTabsInteractor {
  activeCycles: number | null;
  inactiveCycles: number | null;
  completedCycles: number | null;
  isLoading: boolean;

  activeTab: number;
  onChangeTab: (value: number) => void;
  onChangeSearch: (value: string) => void;
}

export const useContractScreensTabsInteractor = (): IContractScreensTabsInteractor => {
  const { clientId, contractId } = useParams();
  const [activeTab, setActiveTab] = useState<number>(NO_TAB_SELECTED);

  const [activeCycles, setActiveCycles] = useState<number>(null);
  const [inactiveCycles, setInactiveCycles] = useState<number>(null);
  const [completedCycles, setCompletedCycles] = useState<number>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const { cyclesCount } = useAppSelector((state) => state.cycles);

  const {
    locations: { getLocation },
    contracts: { getContract },
    cycles: {
      getCyclesCount,
      setSearchCycleQuery,
      resetCompletedCycles,
      resetInactiveCycles,
      resetScreenCycles,
      resetScreenlessCycles,
      resetSearchCycleQuery,
    },
  } = useAppDispatch();

  const updateStates = (data: CyclesCountResponse | null) => {
    if (!data) {
      return;
    }

    setActiveCycles(data.active);
    setInactiveCycles(data.inactive);
    setCompletedCycles(data.completed);
  };

  const fetchDataByContract = async (contractId: string) => {
    try {
      setIsLoading(true);

      await getCyclesCount(contractId);
      await getContract(contractId);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const setStatusQuery = (status: CycleStatus) => {
    setSearchParams({
      [CYCLE_TAB_BY_STATUS_QUERY_KEY]: status,
    });
  };

  const setupDefaultTab = () => {
    setStatusQuery(DEFAULT_STATUS_TAB);
  };

  const setActiveTabByParams = (urLSearchParams: URLSearchParams) => {
    const data = urLSearchParams.get(CYCLE_TAB_BY_STATUS_QUERY_KEY);

    if (!(data in CYCLE_STATUS_TO_NUMBER)) {
      setupDefaultTab();
    }

    const activeTab = CYCLE_STATUS_TO_NUMBER[data];

    setActiveTab(activeTab);
  };

  const onChangeActiveTab: IContractScreensTabsInteractor['onChangeTab'] = (value: number) => {
    if (!(value in TAB_NUMBER_TO_STATUS)) {
      return;
    }

    const currentStatus = TAB_NUMBER_TO_STATUS[value];

    setStatusQuery(currentStatus);
  };

  const resetStore = () => {
    const fns = [
      resetCompletedCycles,
      resetInactiveCycles,
      resetScreenCycles,
      resetScreenlessCycles,
      resetSearchCycleQuery,
    ];

    fns.forEach((fn) => fn());
  };

  usePolling(() => fetchDataByContract(contractId), EXTENDED_INTERVAL_UPDATE_DATA, [contractId]);

  useEffect(() => {
    getLocation(clientId);
  }, [clientId]);

  useEffect(() => {
    setActiveTabByParams(searchParams);
  }, [searchParams]);

  useEffect(() => {
    updateStates(cyclesCount);
  }, [cyclesCount]);

  useEffect(() => {
    return () => {
      resetStore();
    };
  }, []);

  return {
    activeCycles,
    inactiveCycles,
    completedCycles,
    isLoading,
    activeTab,
    onChangeTab: onChangeActiveTab,
    onChangeSearch: setSearchCycleQuery,
  };
};
